import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';
import ApiService from '../services/api';
import { Account, CreateAccountRequest } from '../types';

// Validation schema
const accountSchema = yup.object({
  account_id: yup.string().required('Account ID is required').length(12, 'Account ID must be 12 digits'),
  environment: yup.string().required('Environment is required'),
  region: yup.string().required('Region is required'),
  cognito_user_pool_id: yup.string().required('User Pool ID is required'),
  deployment_name: yup.string().required('Deployment name is required'),
  login_url: yup.string().optional().url('Login URL must be a valid URL'),
  email_type: yup.string().oneOf(['amplify', 'majk'], 'Email type must be Amplify or Majk').optional(),
  download_link_mac: yup.string().optional().url('Mac download link must be a valid URL'),
  download_link_windows: yup.string().optional().url('Windows download link must be a valid URL'),
});

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [testingAccount, setTestingAccount] = useState<string | null>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<CreateAccountRequest>({
    resolver: yupResolver(accountSchema),
    defaultValues: {
      account_id: '',
      environment: 'dev',
      region: 'us-east-1',
      cognito_user_pool_id: '',
      deployment_name: '',
      login_url: '',
      email_type: 'amplify',
      download_link_mac: '',
      download_link_windows: '',
    },
  });

  const emailType = watch('email_type');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getAccounts();
      setAccounts(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load accounts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateAccountRequest) => {
    try {
      setError(null);
      if (editingAccount) {
        await ApiService.updateAccount(editingAccount.account_id, data);
        setSuccess('Account updated successfully');
      } else {
        await ApiService.createAccount(data);
        setSuccess('Account created successfully');
      }
      setDialogOpen(false);
      setEditingAccount(null);
      reset();
      await loadAccounts();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save account';
      setError(errorMessage);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    reset({
      account_id: account.account_id,
      environment: account.environment,
      region: account.region,
      cognito_user_pool_id: account.cognito_user_pool_id,
      deployment_name: account.deployment_name,
      login_url: account.login_url || '',
      email_type: account.email_type || 'amplify',
      download_link_mac: account.download_link_mac || '',
      download_link_windows: account.download_link_windows || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      setError(null);
      await ApiService.deleteAccount(accountId);
      setSuccess('Account deleted successfully');
      await loadAccounts();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete account';
      setError(errorMessage);
    }
  };

  const handleTest = async (accountId: string) => {
    try {
      setTestingAccount(accountId);
      setError(null);
      const result = await ApiService.testAccountConnectivity(accountId);
      if (result.connectivity === 'success') {
        setSuccess('Account connectivity test passed');
      } else {
        setError('Account connectivity test failed');
      }
      await loadAccounts();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Connectivity test failed';
      setError(errorMessage);
    } finally {
      setTestingAccount(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    reset();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'disabled':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'deployment_name',
      headerName: 'Deployment Name',
      width: 150,
      sortable: true,
    },
    {
      field: 'account_id',
      headerName: 'Account ID',
      width: 120,
      sortable: true,
    },
    {
      field: 'environment',
      headerName: 'Environment',
      width: 120,
      sortable: true,
    },
    {
      field: 'region',
      headerName: 'Region',
      width: 120,
      sortable: true,
    },
    {
      field: 'email_type',
      headerName: 'Email Type',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value === 'majk' ? 'Majk' : 'Amplify'}
          color={params.value === 'majk' ? 'secondary' : 'primary'}
          size="small"
          variant={params.value === 'majk' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'login_url',
      headerName: 'Login URL',
      width: 200,
      sortable: true,
      renderCell: (params) => {
        const account = params.row;

        if (account.email_type === 'majk') {
          const macLink = account.download_link_mac;
          const windowsLink = account.download_link_windows;

          if (!macLink && !windowsLink) {
            return <span style={{ color: '#999' }}>No download links</span>;
          }

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {macLink && (
                <a href={macLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.8rem' }}>
                  🍎 Mac: {macLink.length > 15 ? `${macLink.substring(0, 15)}...` : macLink}
                </a>
              )}
              {windowsLink && (
                <a href={windowsLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.8rem' }}>
                  🪟 Win: {windowsLink.length > 15 ? `${windowsLink.substring(0, 15)}...` : windowsLink}
                </a>
              )}
            </Box>
          );
        } else {
          const url = account.login_url;
          return url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
              Login: {url.length > 20 ? `${url.substring(0, 20)}...` : url}
            </a>
          ) : (
            <span style={{ color: '#999' }}>-</span>
          );
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'last_sync',
      headerName: 'Last Sync',
      width: 180,
      sortable: true,
      renderCell: (params) => (
        params.value ? format(new Date(params.value), 'MMM dd, yyyy HH:mm') : 'Never'
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      sortable: true,
      renderCell: (params) => (
        format(new Date(params.value), 'MMM dd, yyyy HH:mm')
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Test connectivity">
              <TestIcon />
            </Tooltip>
          }
          label="Test"
          onClick={() => handleTest(params.row.account_id)}
          disabled={testingAccount === params.row.account_id}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit account">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete account">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDelete(params.row.account_id)}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Accounts
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAccounts}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={accounts}
          columns={columns}
          getRowId={(row) => row.account_id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreate)}>
          <DialogTitle>
            {editingAccount ? 'Edit Account' : 'Add New Account'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="account_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account ID"
                    error={!!errors.account_id}
                    helperText={errors.account_id?.message}
                    disabled={!!editingAccount}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="deployment_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Deployment Name"
                    error={!!errors.deployment_name}
                    helperText={errors.deployment_name?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="environment"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Environment</InputLabel>
                    <Select
                      {...field}
                      label="Environment"
                      error={!!errors.environment}
                    >
                      <MenuItem value="dev">Development</MenuItem>
                      <MenuItem value="staging">Staging</MenuItem>
                      <MenuItem value="prod">Production</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Region</InputLabel>
                    <Select
                      {...field}
                      label="Region"
                      error={!!errors.region}
                    >
                      <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                      <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                      <MenuItem value="eu-west-1">Europe (Ireland)</MenuItem>
                      <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="cognito_user_pool_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Cognito User Pool ID"
                    error={!!errors.cognito_user_pool_id}
                    helperText={errors.cognito_user_pool_id?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="login_url"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={emailType === 'majk' ? 'Login URL (Optional)' : 'Login URL (Optional)'}
                    error={!!errors.login_url}
                    helperText={errors.login_url?.message || 'URL for accessing this environment/account'}
                    fullWidth
                    placeholder="https://myapp.example.com"
                    disabled={emailType === 'majk'}
                  />
                )}
              />

              <Controller
                name="email_type"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Email Template Type</FormLabel>
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) => {
                        field.onChange(e);
                        // Clear login_url if switching to Majk
                        if (e.target.value === 'majk') {
                          // Don't clear the field, just disable it
                        }
                      }}
                    >
                      <FormControlLabel
                        value="amplify"
                        control={<Radio />}
                        label="Amplify"
                      />
                      <FormControlLabel
                        value="majk"
                        control={<Radio />}
                        label="Majk"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />

              <Collapse in={emailType === 'majk'}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Controller
                    name="download_link_mac"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Mac Download Link (Optional)"
                        error={!!errors.download_link_mac}
                        helperText={
                          errors.download_link_mac?.message ||
                          'URL where users can download Majk for Mac'
                        }
                        fullWidth
                        placeholder="https://download.majk.app/mac"
                      />
                    )}
                  />
                  <Controller
                    name="download_link_windows"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Windows Download Link (Optional)"
                        error={!!errors.download_link_windows}
                        helperText={
                          errors.download_link_windows?.message ||
                          'URL where users can download Majk for Windows'
                        }
                        fullWidth
                        placeholder="https://download.majk.app/windows"
                      />
                    )}
                  />
                  <Typography variant="caption" color="textSecondary">
                    💡 If no download links are provided, no download buttons will be shown in emails
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingAccount ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Accounts;